import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, CheckCircle, Zap, Database, Brain } from 'lucide-react';
import { siteConfig } from '@/siteConfig';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';

export const Home = () => {
  const { language, t } = useLanguage();
  const seo = siteConfig.seo.home;

  useEffect(() => {
    document.title = t(seo.title);
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute('content', t(seo.description));
    }
  }, [language, seo, t]);

  return (
    <>

      <div className="flex flex-col">
        {/* Hero Section - Corporate Industrial Design */}
        <section 
          className="relative min-h-[85vh] flex items-center overflow-hidden"
          data-testid="hero-section"
        >
          {/* Background Image with Overlay */}
          <div className="absolute inset-0 z-0">
            <img
              src="https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?w=1920&q=80"
              alt=""
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-background via-background/95 to-background/70 dark:from-background dark:via-background/98 dark:to-background/80" />
          </div>
          
          {/* Content */}
          <div className="container mx-auto px-6 md:px-12 lg:px-16 relative z-10">
            <div className="max-w-3xl">
              {/* Company Badge */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 text-primary text-sm font-medium mb-8"
              >
                <span className="w-2 h-2 bg-primary rounded-full animate-pulse" />
                {language === 'fr' ? 'Partout au Canada' : 'Across Canada'}
              </motion.div>

              {/* Main Title */}
              <motion.h1
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                className="font-heading text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-black mb-6 tracking-tight leading-[1.1]"
                data-testid="hero-title"
              >
                <span className="text-foreground">{siteConfig.brand.companyName}</span>
              </motion.h1>

              {/* Tagline */}
              <motion.p
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="text-xl md:text-2xl lg:text-3xl text-primary font-heading font-semibold mb-4"
              >
                {t(siteConfig.brand.tagline)}
              </motion.p>

              {/* Description */}
              <motion.p
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
                className="text-base md:text-lg text-muted-foreground mb-10 leading-relaxed max-w-2xl"
                data-testid="hero-subtitle"
              >
                {t(siteConfig.home.hero.subtitle)}
              </motion.p>

              {/* CTA Buttons */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                className="flex flex-col sm:flex-row gap-4"
              >
                <Button
                  asChild
                  size="lg"
                  className="bg-accent hover:bg-accent/90 text-white font-bold uppercase tracking-wider px-8 py-6 text-base shadow-lg shadow-accent/25 hover:shadow-xl hover:shadow-accent/30 transition-all duration-300"
                  data-testid="hero-primary-cta"
                >
                  <Link to="/contact">
                    {t(siteConfig.brand.primaryCTA)}
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Link>
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  className="font-semibold uppercase tracking-wider border-2 px-8 py-6 text-base hover:bg-foreground hover:text-background transition-all duration-300"
                  data-testid="hero-secondary-cta"
                  onClick={() => {
                    // Find and click the chat toggle button
                    const chatBtn = document.querySelector('[data-testid="chat-toggle-button"]');
                    if (chatBtn) chatBtn.click();
                  }}
                >
                  {t(siteConfig.brand.secondaryCTA)}
                </Button>
              </motion.div>

              {/* Trust Indicators */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.6, delay: 0.6 }}
                className="mt-12 pt-8 border-t border-border/50"
              >
                <div className="flex flex-wrap gap-6 text-sm text-muted-foreground">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-primary" />
                    <span>{language === 'fr' ? 'CSA Conformité' : 'CSA Compliance'}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-primary" />
                    <span>{language === 'fr' ? '600V Industriel' : '600V Industrial'}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-primary" />
                    <span>{language === 'fr' ? 'Support Technique 24/7' : '24/7 Technical Support'}</span>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Services Overview */}
        <section className="py-20 md:py-32" data-testid="services-section">
          <div className="container mx-auto px-4 md:px-8 lg:px-12">
            <div className="text-center mb-12">
              <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
                {language === 'fr' ? 'Ce que nous faisons' : 'What We Do'}
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                {language === 'fr'
                  ? 'Solutions complètes pour vos projets d’automatisation industrielle'
                  : 'Complete solutions for your industrial automation projects'}
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {siteConfig.services.map((service, idx) => (
                <motion.div
                  key={service.key}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: idx * 0.1 }}
                  className="flex"
                >
                  <Card className="group hover:border-primary/50 transition-colors duration-300 w-full flex flex-col">
                    <CardContent className="p-6 flex flex-col h-full">
                      <div className="mb-4">
                        <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                          {service.icon === 'FileText' && <Zap className="h-6 w-6 text-primary" />}
                          {service.icon === 'Box' && <Database className="h-6 w-6 text-primary" />}
                          {service.icon === 'Cpu' && <Brain className="h-6 w-6 text-primary" />}
                        </div>
                      </div>
                      <h3 className="font-heading text-lg font-semibold mb-4 line-clamp-2 min-h-[56px]">
                        {t(service.title)}
                      </h3>
                      <ul className="space-y-2 mb-6 flex-1">
                        {(language === 'fr' ? service.bulletsFR : service.bulletsEN).map((bullet, i) => (
                          <li key={i} className="flex items-start gap-2 text-sm text-muted-foreground">
                            <CheckCircle className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
                            <span>{bullet}</span>
                          </li>
                        ))}
                      </ul>
                      <Button
                        asChild
                        variant="outline"
                        className="w-full mt-auto"
                        data-testid={`service-cta-${service.key}`}
                      >
                        <Link to="/contact">
                          {language === 'fr' ? 'Obtenir une estimation' : 'Get an Estimate'}
                        </Link>
                      </Button>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Differentiator */}
        <section className="py-20 md:py-32 bg-muted/30" data-testid="differentiator-section">
          <div className="container mx-auto px-4 md:px-8 lg:px-12">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="font-heading text-3xl md:text-4xl font-bold mb-6">
                {language === 'fr'
                  ? 'Automatisation prête pour l’IA'
                  : 'AI-Ready Automation'}
              </h2>
              <p className="text-lg text-muted-foreground leading-relaxed">
                {language === 'fr'
                  ? 'Programmes PLC structurés pour un dépannage plus rapide, des séquences claires et des agents IA futurs. Nos systèmes sont conçus pour la performance d’aujourd’hui et l’intelligence de demain.'
                  : 'Structured PLC programs for faster troubleshooting, clearer sequences, and future AI agents. Our systems are designed for today’s performance and tomorrow’s intelligence.'}
              </p>
            </div>
          </div>
        </section>

        {/* Industry 4.0 */}
        <section className="py-20 md:py-32" data-testid="industry4-section">
          <div className="container mx-auto px-4 md:px-8 lg:px-12">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="font-heading text-3xl md:text-4xl font-bold mb-6">
                  {t(siteConfig.industry4.title)}
                </h2>
                <p className="text-lg text-muted-foreground mb-6 leading-relaxed italic">
                  {t(siteConfig.home.premiumLine)}
                </p>
                <ul className="space-y-3">
                  {t(siteConfig.industry4.bullets).map((bullet, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <CheckCircle className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                      <span className="text-muted-foreground">{bullet}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="relative h-64 lg:h-96 rounded-lg overflow-hidden shadow-xl">
                <img
                  src="https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?w=800&q=80"
                  alt="Industry 4.0"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Industry 5.0 */}
        <section className="py-20 md:py-32 bg-muted/30" data-testid="industry5-section">
          <div className="container mx-auto px-4 md:px-8 lg:px-12">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div className="order-2 lg:order-1 relative h-64 lg:h-96 rounded-lg overflow-hidden shadow-xl">
                <img
                  src="https://images.unsplash.com/photo-1581092160562-40aa08e78837?w=800&q=80"
                  alt="Industry 5.0"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="order-1 lg:order-2">
                <h2 className="font-heading text-3xl md:text-4xl font-bold mb-6">
                  {t(siteConfig.industry5.title)}
                </h2>
                <ul className="space-y-3">
                  {t(siteConfig.industry5.bullets).map((bullet, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <CheckCircle className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                      <span className="text-muted-foreground">{bullet}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Industries Grid */}
        <section className="py-20 md:py-32" data-testid="industries-section">
          <div className="container mx-auto px-4 md:px-8 lg:px-12">
            <div className="text-center mb-12">
              <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
                {language === 'fr' ? 'Secteurs desservis' : 'Industries Served'}
              </h2>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {siteConfig.industries[language].map((industry, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.3, delay: idx * 0.05 }}
                >
                  <Card className="h-full hover:border-primary/50 transition-colors duration-300">
                    <CardContent className="p-4 text-center">
                      <p className="text-sm font-medium">{industry}</p>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Projects Teaser */}
        <section className="py-20 md:py-32 bg-muted/30" data-testid="projects-teaser-section">
          <div className="container mx-auto px-4 md:px-8 lg:px-12">
            <div className="text-center mb-12">
              <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
                {language === 'fr' ? 'Réalisations récentes' : 'Recent Projects'}
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-8">
              {siteConfig.projects.slice(0, 3).map((project, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: idx * 0.1 }}
                  className="flex"
                >
                  <Card className="overflow-hidden group hover:border-primary/50 transition-colors duration-300 w-full flex flex-col">
                    <div className="h-64 relative overflow-hidden flex-shrink-0">
                      <img
                        src={project.image}
                        alt={t(project.title)}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                    <CardContent className="p-6 flex-1 flex flex-col">
                      <div className="text-xs font-semibold text-primary mb-2 uppercase tracking-wide">
                        {t(project.industry)}
                      </div>
                      <h3 className="font-heading text-lg font-semibold mb-2 line-clamp-2 min-h-[56px]">
                        {t(project.title)}
                      </h3>
                      <p className="text-sm text-muted-foreground mb-3 flex-1">{t(project.outcome)}</p>
                      <p className="text-xs text-muted-foreground">{project.technologies}</p>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
            <div className="text-center">
              <Button asChild variant="outline" size="lg" data-testid="view-all-projects-link">
                <Link to={language === 'fr' ? '/projets' : '/projects'}>
                  {language === 'fr' ? 'Voir tous les projets' : 'View All Projects'}
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="py-20 md:py-32 bg-primary text-primary-foreground" data-testid="final-cta-section">
          <div className="container mx-auto px-4 md:px-8 lg:px-12 text-center">
            <h2 className="font-heading text-3xl md:text-4xl font-bold mb-6">
              {language === 'fr'
                ? 'Prêt à démarrer votre projet?'
                : 'Ready to Start Your Project?'}
            </h2>
            <p className="text-lg mb-8 opacity-90 max-w-2xl mx-auto">
              {language === 'fr'
                ? 'Contactez-nous dès aujourd’hui pour discuter de vos besoins en automatisation industrielle.'
                : 'Contact us today to discuss your industrial automation needs.'}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                asChild
                size="lg"
                variant="secondary"
                className="font-bold uppercase tracking-wide"
              >
                <Link to="/contact">{t(siteConfig.brand.primaryCTA)}</Link>
              </Button>
              <Button
                asChild
                size="lg"
                variant="outline"
                className="bg-transparent border-2 border-primary-foreground text-primary-foreground hover:bg-primary-foreground hover:text-primary font-semibold uppercase tracking-wide"
              >
                <Link to={language === 'fr' ? '/a-propos' : '/about'}>
                  {language === 'fr' ? 'En savoir plus' : 'Learn More'}
                </Link>
              </Button>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};